import React from 'react';
import useSharePdf from '../hooks/useSharePdf';
import { MENU_ITEM_NEUTRAL } from './OrderActionsMenu';

/**
 * THE `Download list (PDF)` + `Print all` rows of a list page's `Actions ▾`,
 * over ANY list document. Moved VERBATIM out of `OrderListPrintMenuItem.js`
 * (2026-09-27) when the hires pages needed the same two rows for hire lines —
 * that component is now the ORDER document handed to this one, and renders the
 * same DOM it always did (its callers' suites are the proof). Read
 * `OrderListPrintMenuItem.js`'s docblock for the rulings behind every line here:
 * ONE `useSharePdf` feeds both rows; no `orientation` option (inert on the
 * pdfmake path — landscape comes from the doc def); no permission gate (the
 * document is built client-side from rows already on screen); `Download list
 * (PDF)` renders only with a `downloadTestId`, ABOVE `Print all`; an EMPTY list
 * still renders the rows (the document says "No … match the current filters").
 *
 * Props:
 *  - renderPdfMakeDoc: `() => docDefinition` — the caller's list document,
 *    built from the FILTERED rows, every page (never the page slice).
 *  - filename / title: passed to `useSharePdf`.
 *  - rowNoun: the singular record word in the rows' tooltips ("order", "hire").
 *  - onDone / testId / downloadTestId: as `OrderListPrintMenuItem`.
 */
export default function ListPrintMenuItems({
  renderPdfMakeDoc,
  filename,
  title,
  rowNoun,
  onDone,
  testId,
  downloadTestId,
}) {
  const { pendingAction, handlePrint, handleDownload } = useSharePdf({
    renderPdfMakeDoc,
    filename,
    title,
    onActionComplete: onDone,
  });

  return (
    <>
      {downloadTestId && (
        <button
          type="button"
          role="menuitem"
          onClick={(e) => { e.stopPropagation(); handleDownload(); }}
          disabled={!!pendingAction}
          data-testid={downloadTestId}
          title={`Download every ${rowNoun} matching the current filters as a landscape PDF list`}
          className={MENU_ITEM_NEUTRAL}
        >
          {pendingAction === 'download' ? 'Generating…' : 'Download list (PDF)'}
        </button>
      )}
      <button
        type="button"
        role="menuitem"
        onClick={(e) => { e.stopPropagation(); handlePrint(); }}
        disabled={!!pendingAction}
        data-testid={testId}
        title={`Print every ${rowNoun} matching the current filters, as a landscape list`}
        className={MENU_ITEM_NEUTRAL}
      >
        {pendingAction === 'print' ? 'Generating…' : 'Print all'}
      </button>
    </>
  );
}
